// Core lib dependencies.
import React from 'react';
import PropTypes from 'prop-types';

// Shared utils for Display Items.
import * as utils from '../utils.jsx';
import * as ItemUtils from '../utils';

// Misc dependencies.
import { isVideoOrGif, getDisplayAssets } from '../../../utils/asset';
import Enums from '../../../../apps/UgcWall/config/enums';

// Model objects.
import DisplayItem from '../../../model/entity/DisplayItem/DisplayItem.js';
import PinnedDisplayItem from '../../../model/entity/PinnedDisplayItem.js';

import _ from 'lodash';

/**
 * Media layer Display Item.
 *
 * @namespace
 * @memberOf Common.Components.DisplayItems.Items
 */
class MediaLayer extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
    this.setItemRef = this.setItemRef.bind(this);
    this.getCopyBodyHeight = this.getCopyBodyHeight.bind(this);
    this.getBylineHeight = this.getBylineHeight.bind(this);
    this.getHeadingHeight = this.getHeadingHeight.bind(this);
    this.state = {
      height:         undefined,
      copyBodyHeight: undefined,
      bylineHeight:   undefined,
      headingHeight:  undefined,
    };
  }

  componentDidMount() {
    const height = this.itemRef.offsetHeight;
    const copyBodyHeight = this.getCopyBodyHeight(height);
    const bylineHeight = this.getBylineHeight(copyBodyHeight);
    const headingHeight = this.getHeadingHeight(copyBodyHeight);

    if (height > 0) {
      this.setState({ copyBodyHeight, height, bylineHeight, headingHeight });
    }
  }

  componentDidUpdate() {
    const height = this.itemRef.offsetHeight;
    if (height !== this.state.height) {
      const copyBodyHeight = this.getCopyBodyHeight(height);
      const bylineHeight = this.getBylineHeight(copyBodyHeight);
      const headingHeight = this.getHeadingHeight(copyBodyHeight);
      if (height > 0) {
        this.setState({ copyBodyHeight, height, bylineHeight, headingHeight });
      }
    }
  }

  setItemRef(n) {
    this.itemRef = n;
  }

  // gets the space alloted for copy byline, takes precedence for space over content body
  getBylineHeight(copyBodyHeight) {
    const byline = this.itemRef.querySelector('.xByline');
    const bylineSpace = !!byline ? byline.offsetHeight : 0;
    return bylineSpace + copyBodyHeight;
  }

  // gets the space alloted for heading, takes precedence for space over byline and content body
  // if alloted space is less than lineHeight of heading, than return 0 as alloted space to hide header
  getHeadingHeight(copyBodyHeight) {
    const heading = this.itemRef.querySelector('.xHeading');
    const headingHeight = !!heading ? heading.offsetHeight : 0;
    const allotedSpace = headingHeight + this.getBylineHeight(copyBodyHeight);
    const headingLineHeight = !!heading ? parseInt(window.getComputedStyle(heading).lineHeight, 10) : 0;
    return allotedSpace < headingLineHeight ? 0 : allotedSpace;
  }

  // gets the space alloted for copy body after taking all other display elements into account
  getCopyBodyHeight(totalHeight) {
    // FF does not recognize shorthand 'padding' as a computed style
    const itemInner = this.itemRef.querySelector('.xItemInner');
    const itemPadding = parseInt(window.getComputedStyle(itemInner)['padding-top'], 10) * 2;

    const profile = this.itemRef.querySelector('.xProfileWrapper');
    const profileSpace = !!profile ?
      profile.offsetHeight + parseInt(window.getComputedStyle(profile).marginBottom, 10) :
      0;

    const heading = this.itemRef.querySelector('.xHeading');
    const headingSpace = !!heading ?
      heading.offsetHeight + parseInt(window.getComputedStyle(heading).marginBottom, 10) :
      0;

    const byline = this.itemRef.querySelector('.xByline');
    const bylineSpace = !!byline ? byline.offsetHeight + parseInt(window.getComputedStyle(byline).marginBottom, 10) : 0;

    const vote = this.itemRef.querySelector('.xActionPlatform');
    let voteSpace = !!vote ? vote.offsetHeight + parseInt(window.getComputedStyle(vote).marginBottom, 10) : 0;

    const date = this.itemRef.querySelector('.xMetaDate');
    const dateSpace = !!date ? date.offsetHeight + parseInt(window.getComputedStyle(date).marginBottom, 10) : 0;

    // voting and date space overlap (absolutely positioned), so when both are present don't take voteSpace into account
    if (!!date && !!vote) {
      voteSpace = 0;
    }

    const action = this.itemRef.querySelector('.xActionNetwork');
    const actionSpace = !!action ? action.offsetHeight : 0;

    const copyContainer = this.itemRef.querySelector('.xCopyContainer');
    const copyMargin = !!copyContainer ? parseInt(window.getComputedStyle(copyContainer).marginBottom, 10) : 0;

    return (
      totalHeight -
      itemPadding -
      profileSpace -
      dateSpace -
      voteSpace -
      actionSpace -
      headingSpace -
      bylineSpace -
      copyMargin
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    const showOriginalHandle = true;
    const _profile = utils.getItemProfile({
      item:                this.props.item,
      onExternalLinkClick: this.props.onExternalLinkClick,
      showOriginalHandle,
    });
    const shouldAutoPlay = !_.isEmpty(this.props.item.originalContent) ?
      ItemUtils.shouldAutoPlay(this.props.moduleOpts.autoPlayConfig, this.props.item.originalContent) :
      ItemUtils.shouldAutoPlay(this.props.moduleOpts.autoPlayConfig, this.props.item);
    const originalContentAsset = utils.getItemAsset({
      item:        this.props.item.originalContent,
      assetUrl:    this.props.assetUrl,
      assetUgcUrl: this.props.assetUgcUrl,
      moduleOpts:  this.props.moduleOpts,
      itemOpts:    this.props.itemOpts,
    });
    const _mainContent = utils.getItemContent({
        item:      this.props.item,
        type:      'main',
        heightMap: {
          body:    this.state.copyBodyHeight,
          byline:  this.state.bylineHeight,
          heading: this.state.headingHeight,
        },
        onExternalLinkClick: this.props.onExternalLinkClick,
        truncateHeader:      true,
      }),
      _contentTypeMediaIcon = getItemContentTypeMediaIcon(this.props.item),
      _networkIcon = utils.getNetworkIcon(this.props.item.meta.network),
      _timestamp = utils.getItemTimestamp({
        item:                this.props.item,
        onExternalLinkClick: this.props.onExternalLinkClick,
      });

    const fontSize = this.props.style ? this.props.style.fontSize : null;
    let innerStyle = { fontSize };

    // TODO: remove this TEMP hack when WYN-1603 (smart image sizing) is done.
    const res =
      this.props.moduleOpts.type === Enums.COLLAGE || this.props.moduleOpts.type === Enums.SOCIAL_CONTENT_CAROUSEL ?
        'medium' :
        'small';
    if (!shouldAutoPlay) {
      innerStyle = mergeItemAssetInnerStyle(
        this.props.item,
        innerStyle,
        this.props.assetUrl,
        this.props.assetUgcUrl,
        res
      );
    }
    const shouldRenderItemAssetBool = shouldRenderItemAsset(
      shouldAutoPlay,
      this.props.item,
      innerStyle.backgroundImage
    );

    const displayItemModel = {
      'data-ngx-tags': this.props.item.meta.tags,
      'data-ngx-id':   this.props.item.id,
      onClick:         () => {
        this.props.onClick(this.props.item);
      },
      ref: this.setItemRef,
    };

    const className = utils.getClassNamesForItem({
      item:          this.props.item,
      templateOpts:  this.props.templateOpts,
      itemOpts:      this.props.itemOpts,
      customVariant: this.props.customVariant,
      tabIndex:      this.props.tabIndex,
    });
    return (
      <div {...displayItemModel} className={className} tabIndex={this.props.tabIndex}>
        <div className="xItemInner" style={innerStyle}>
          {shouldRenderItemAssetBool && (this.props.itemAsset || originalContentAsset)}
          {_profile}
          {_mainContent}
          {!shouldRenderItemAssetBool && _contentTypeMediaIcon}
          {_timestamp}
          {this.props.platformActions}
          {_networkIcon}
          {this.props.networkActions}
          {this.props.sharingActions}
        </div>
      </div>
    );
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Common.Model.Entity.DisplayItem} item The DisplayItem object.
 */
MediaLayer.propTypes = {
  item: PropTypes.oneOfType([
    PropTypes.instanceOf(DisplayItem),
    PropTypes.instanceOf(PinnedDisplayItem),
  ]),
  itemOpts:            PropTypes.object,
  templateOpts:        PropTypes.object,
  moduleOpts:          PropTypes.object,
  style:               PropTypes.object, // proportional font size
  customVariant:       PropTypes.string,
  onExternalLinkClick: PropTypes.func,
  tabIndex:            PropTypes.number,
  platformActions:     PropTypes.element,
  networkActions:      PropTypes.element,
  sharingActions:      PropTypes.element,
};

/**
 * React default props.
 *
 * @type {Object}
 */
MediaLayer.defaultProps = {
  item:            {},
  templateOpts:    {},
  itemOpts:        {},
  style:           {},
  customVariant:   undefined,
  tabIndex:        0,
  platformActions: null,
  networkActions:  null,
  sharingActions:  null,
};

function getItemContentTypeMediaIcon(item) {
  if (isVideoOrGif(item.meta.contentType)) {
    return (
      <div className="xMediaContainer xPlayable xMediaVideo">
        <i className="xIcon xIconPlay" />
      </div>
    );
  }
}

/**
 * Merges any required styles for a given items asset to the innerStyle object.
 *
 * @private
 *
 * @param  item {object}
 * @param  innerStyle {object}
 * @returns {object}
 */
function mergeItemAssetInnerStyle(item, innerStyle, assetUrl, assetUgcUrl, res) {
  const displayAssets = getDisplayAssets(item, assetUrl, assetUgcUrl, res);
  const originalDisplayAssets = getDisplayAssets(item.originalContent, assetUrl, assetUgcUrl, res);

  // use original content media as background if present
  innerStyle.backgroundImage = getBackgroundImage(displayAssets) || getBackgroundImage(originalDisplayAssets);

  return innerStyle;
}

function getBackgroundImage(assets) {
  if (!!_.get(assets, ['thumbnail', 'src'])) {
    if (!_.isEmpty(_.get(assets, ['thumbnail', 'sizes']))) {
      return `url('${_.get(assets, ['thumbnail', 'sizes', 'small', 'src'])}')`;
    } else {
      return 'url(\'' + assets.thumbnail.formattedThumbnailAsset + '\')';
    }
  } else {
    return null;
  }
}

function shouldRenderItemAsset(shouldAutoPlay, item, innerStyleBackgroundImage) {
  return (isVideoOrGif(item.meta.contentType) && innerStyleBackgroundImage === undefined) || shouldAutoPlay;
}

export default MediaLayer;
